import { useApp } from '../context.js';
import { Edit3, Star, Trash2 } from '../icons.jsx';
import { formatLocalDate } from '../lib/date.js';
import { visitPairs, canSeeScore, partnerOf } from '../lib/visits.js';
import { formatRating } from '../lib/rating.js';

const NAME = { doug: '💛 Doug', les: '💖 Les' };
const CHIP = { doug: 'bg-blue-100 text-blue-700', les: 'bg-pink-100 text-pink-700' };

// One row per night out. Each of you rates blind: your partner's score stays hidden until you rate that night too
// (or Peek, or 48 hours pass).
export default function VisitHistory({ restaurant }) {
    const { currentProfile, deleteVisit, openEditVisit, openVisitModal, revealVisit, t } = useApp();
    const pairs = visitPairs(restaurant.visits);
    const partner = partnerOf(currentProfile);

    const entry = (visit, who, hidden) => (
        <div key={visit.id} className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${CHIP[who] || CHIP.doug}`}>{NAME[who] || NAME.doug}</span>
                    {hidden ? (
                        <span className="text-sm text-gray-500">rated 🙈</span>
                    ) : (
                        <div className={`flex items-center gap-1 ${t.accentBg} px-2 py-1 rounded-full`}>
                            <Star size={12} className="text-yellow-500 fill-yellow-500" />
                            <span className="text-sm font-bold text-gray-700">{formatRating(visit.rating)}/10</span>
                        </div>
                    )}
                </div>
                {hidden ? (
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <span className="text-xs text-gray-500">Log yours to see it</span>
                        <button onClick={() => revealVisit(restaurant.id, visit.id)} className="text-xs font-semibold text-pink-600 underline underline-offset-2 py-1">
                            Peek
                        </button>
                    </div>
                ) : visit.notes ? (
                    <p className="text-sm text-gray-700 mt-1">{visit.notes}</p>
                ) : null}
            </div>
            {who === currentProfile && (
                <div className="flex gap-1">
                    <button
                        onClick={() => openEditVisit(restaurant.id, visit)}
                        className="text-gray-400 hover:text-blue-600 p-1 apple-button touch-target"
                        aria-label="Edit visit"
                    >
                        <Edit3 size={14} />
                    </button>
                    <button
                        onClick={() => deleteVisit(restaurant.id, visit.id)}
                        className="text-red-400 hover:text-red-600 p-1 apple-button touch-target"
                        aria-label="Delete visit"
                    >
                        <Trash2 size={14} />
                    </button>
                </div>
            )}
        </div>
    );

    return (
        <div className="pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-bold text-gray-700">📝 Visit History:</h4>
                <button
                    onClick={() => openVisitModal(restaurant.id)}
                    className={`bg-gradient-to-r ${t.accent} text-white px-3 py-2 rounded-lg font-semibold text-sm apple-button`}
                >
                    + Log Visit
                </button>
            </div>
            {pairs.length > 0 ? (
                <div className="space-y-2 max-h-72 overflow-y-auto">
                    {pairs.map((pair) => {
                        const visible = canSeeScore(pair, currentProfile);
                        const bothRated = pair.doug && pair.les;
                        return (
                            <div key={pair.date} className="bg-gradient-to-r from-gray-50 to-gray-100 p-3 rounded-xl space-y-2">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold text-gray-500">{formatLocalDate(pair.date)}</span>
                                    {bothRated && visible && (
                                        <span className="text-xs text-gray-500">
                                            You: <b className="text-gray-700">{formatRating(pair[currentProfile].rating)}</b> · {partner === 'les' ? 'Les' : 'Doug'}: <b className="text-gray-700">{formatRating(pair[partner].rating)}</b>
                                        </span>
                                    )}
                                </div>
                                {pair[currentProfile] && entry(pair[currentProfile], currentProfile, false)}
                                {pair[partner] && entry(pair[partner], partner, !visible)}
                                {pair.other.map((v) => entry(v, v.loggedBy, false))}
                                {!pair[currentProfile] && pair[partner] && !visible && (
                                    <button
                                        onClick={() => { openVisitModal(restaurant.id, pair.date); }}
                                        className="text-xs font-semibold text-gray-600 bg-white rounded-lg px-3 py-2 apple-button"
                                    >
                                        Rate this night too
                                    </button>
                                )}
                            </div>
                        );
                    })}
                </div>
            ) : (
                <p className="text-sm text-gray-500 italic text-center py-4">No visits logged yet</p>
            )}
        </div>
    );
}
